import { createReducer } from '@reduxjs/toolkit';
import { records } from '@saucelabs/sl-module-user';
import pick from 'lodash.pick';
import isEqual from 'lodash.isequal';
import { createSelector } from 'reselect';

import {
  changeDateFilterSelection,
  changeFilterSelection,
  changeOwnerFilterSelection,
  fetchUsers,
  fetchTeams,
  fetchFilters,
  setOwnersSynchronized,
  changeAllFiltersSelection,
  setFiltersSynchronized,
  changeActiveFilters,
  resetFilters,
  cleanup,
} from './actions';
import { getDefaultSelection, mapDateToQueryParams, mapOwnerToQueryParams } from './utils';
import {
  Source,
  Filters,
  SelectedFilters,
  DateFilter,
  FilterItem,
  FetchStatus,
  RequestParams,
  FilterKey,
  FilterKeys,
} from './types';
import { Team, User } from './api';

export interface FilterBarState {
  users: {
    byId: Record<string, User>;
  };
  teams: {
    byId: Record<string, Team>;
  };
  filters: Record<Source, Filters>;
  activeFilterKeys: FilterKey[];
  selected: {
    filters: Record<Source, SelectedFilters>;
    date: DateFilter;
    owner?: string[];
  };
  status: {
    users: FetchStatus;
    teams: FetchStatus;
    filters: FetchStatus;
  };
  requestParams: RequestParams;
}

const initialFilters: Filters = {
  build: [],
  os: [],
  browser: [],
  device: [],
  tag: [],
  framework: [],
  deviceGroup: [
    { key: 'public', name: 'public', label: 'Public' },
    { key: 'private', name: 'private', label: 'Private' },
  ],
};
export const initialState: FilterBarState = {
  users: {
    byId: {},
  },
  teams: {
    byId: {},
  },
  filters: {
    vdc: initialFilters,
    rdc: initialFilters,
  },
  activeFilterKeys: Object.values(FilterKeys),
  selected: {
    owner: undefined,
    date: {
      start: undefined,
      end: undefined,
    },
    filters: {
      vdc: {},
      rdc: {},
    },
  },
  status: {
    users: 'idle',
    teams: 'idle',
    filters: 'idle',
  },
  requestParams: {
  },
};

const filterBarReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(changeFilterSelection, (draft, { payload }) => {
      draft.selected.filters[payload.source][payload.key] = payload.values;
      draft.requestParams[payload.key] = payload.values;
    })
    .addCase(changeAllFiltersSelection, (draft, { payload }) => {
      draft.selected.filters[payload.source] = payload.filters;
      draft.requestParams = payload.filters;
    })
    .addCase(changeDateFilterSelection, (draft, { payload }) => {
      if (!isEqual(draft.selected.date, payload)) {
        draft.status.filters = 'idle';
      }
      draft.selected.date = payload;
      const dateRequestParams = mapDateToQueryParams(payload);
      draft.requestParams.start = dateRequestParams.start;
      draft.requestParams.end = dateRequestParams.end;
    })
    .addCase(changeOwnerFilterSelection, (draft, { payload }) => {
      draft.selected.owner = payload.keys;
      const ownerRequestParams = mapOwnerToQueryParams(payload.keys, {
        org_id: payload.userOrgId,
      });
      draft.requestParams.org_id = ownerRequestParams.org_id;
      draft.requestParams.team_id = ownerRequestParams.team_id;
      draft.requestParams.user_id = ownerRequestParams.user_id;
    })
    .addCase(changeActiveFilters, (draft, { payload }) => {
      draft.activeFilterKeys = payload;
    })
    .addCase(fetchUsers.pending, (draft) => {
      draft.status.users = 'loading';
    })
    .addCase(fetchUsers.rejected, (draft) => {
      draft.status.users = 'failed';
    })
    .addCase(fetchUsers.fulfilled, (draft, { payload }) => {
      payload.results.forEach((user) => {
        draft.users.byId[user.id] = user;
      });
      draft.status.users = 'succeeded';
    })
    .addCase(fetchTeams.pending, (draft) => {
      draft.status.teams = 'loading';
    })
    .addCase(fetchTeams.rejected, (draft) => {
      draft.status.teams = 'failed';
    })
    .addCase(fetchTeams.fulfilled, (draft, { payload }) => {
      payload.results.forEach((team) => {
        draft.teams.byId[team.id] = team;
      });
      draft.status.teams = 'succeeded';
    })
    .addCase(fetchFilters.pending, (draft) => {
      draft.status.filters = 'loading';
    })
    .addCase(fetchFilters.rejected, (draft) => {
      draft.status.filters = 'failed';
    })
    .addCase(fetchFilters.fulfilled, (draft, action) => {
      const { source } = action.meta.arg;
      Object.entries(action.payload).forEach(([key, values]) => {
        const newFilterValues = values.map(({ name }) => ({
          name,
          key: name,
          label: name,
        }));

        if (
          draft.filters[source][key] &&
          !isEqual(draft.filters[source][key], newFilterValues)
        ) {
          draft.filters[source][key] = newFilterValues;
        }
      });
      draft.status.filters = 'succeeded';
    })
    .addCase(setOwnersSynchronized, (draft) => {
      draft.status.teams = 'synchronized';
      draft.status.users = 'synchronized';
    })
    .addCase(setFiltersSynchronized, (draft) => {
      draft.status.filters = 'synchronized';
    })
    .addCase(resetFilters, (draft, { payload }) => {
      const defaultSelection = getDefaultSelection(payload.user);
      draft.selected.filters[payload.source] = defaultSelection.filters;
      draft.selected.owner = defaultSelection.owner;
      if (!isEqual(draft.selected.date, defaultSelection.date)) {
        draft.selected.date = defaultSelection.date;
        draft.status.filters = 'idle';
      }
      draft.requestParams = {
        ...defaultSelection.filters,
        ...mapOwnerToQueryParams(defaultSelection.owner, {
          org_id: payload.user.organization.id.toString(),
        }),
        ...mapDateToQueryParams(defaultSelection.date),
      };
    })
    .addCase(cleanup, (draft) => {
      draft.status.teams = 'idle';
      draft.status.users = 'idle';
      draft.status.filters = 'idle';
    });
});

export const selectors = {
  selectOwners: (state): FilterItem[] => (
    state.apps.analytics.filterBar.owner as FilterItem[]
  ),
  selectAllUsers: createSelector(
    [
      (state: any): Record<string, User> => (
        state.apps.analytics.filterBar.users.byId as Record<string, User>
      ),
    ],
    (usersById) => Object.values(usersById)),
  selectAllTeams: createSelector(
    [
      (state: any): Record<string, Team> => (
          state.apps.analytics.filterBar.teams.byId as Record<string, User>
      ),
    ],
    (teamsById) => Object.values(teamsById)),
  selectFilters: createSelector(
    [
      (state: any): Record<Source, Filters> => (
        state.apps.analytics.filterBar.filters as Record<Source, Filters>
      ),
      (state): FilterKey[] => (
        state.apps.analytics.filterBar.activeFilterKeys as FilterKey[]
      ),
      (state, source: Source) => source,
    ],
    (filters, activeKeys, source) => pick(filters[source], activeKeys) as Filters,
  ),
  selectAllFiltersSelection: (state): Record<Source, SelectedFilters> => (
    state.apps.analytics.filterBar.selected.filters as Record<Source, SelectedFilters>
  ),
  selectDateFilterSelection: (state): DateFilter => (
    state.apps.analytics.filterBar.selected.date as DateFilter
  ),
  selectOwnerFilterSelection: (state): string[] | undefined => (
    state.apps.analytics.filterBar.selected.owner as string[] | undefined
  ),
  selectFetchStatus: (state): FilterBarState['status'] => (
    state.apps.analytics.filterBar.status as FilterBarState['status']
  ),
  selectRequestParams: (keys?: FilterKey[]) => (state): RequestParams => {
    const activeFilterKeys = keys ?? state.apps.analytics.filterBar.activeFilterKeys;
    return (
    pick(state.apps.analytics.filterBar.requestParams, [
      'start',
      'end',
      'user_id',
      'team_id',
      'org_id',
      ...activeFilterKeys,
    ] as (keyof RequestParams)[]) as RequestParams
    );
  },
  selectIsIndividualUserAllowed: (state): boolean => {
    const user = state.user as records.UserRecord;
    return user.isOrgAdmin() || user.isJobTeamSharingEnabled();
  },
  selectCurrentUser: (state): records.UserRecord => (
    state.user as records.UserRecord
  ),
  selectIsFiltersReady: (state): boolean => (
    state.apps.analytics.filterBar.status.filters === 'synchronized'
  ),
};

export default filterBarReducer;
